/**
 * End-of-day card capture (GH-947, DEC-056; owner 2026-10-17).
 *
 * "Make it an option to capture transactions end of day (default to immediate). Do it on all card
 * transactions, not just tap to pay, and when we have to do a refund, leave behavior of charging
 * platform fee to org."
 *
 * The wire shapes for the console's setting, the cancel action on an authorized gift, and the
 * capture job. Money never moves on these routes' say-so alone: an authorization is captured by the
 * job and completed by the provider's webhook; a cancel withdraws an authorization nobody has been
 * charged for, and posts nothing.
 */
import { z } from "zod";
import { processorApprovalSchema } from "./processor-approval.js";
export const CARD_CAPTURE_TIMING_VALUES = ["immediate", "end_of_day"];
/**
 * `GET|PUT /v1/organizations/:organizationId/card-capture` — owner and admin
 * (`organization.update_profile`). Every card gift (online card, Apple Pay, Google Pay, one-tap,
 * Tap to Pay) follows it; bank debits, held gifts and ticket orders are always captured at once.
 *
 * `timeZone` is an IANA name (`America/Chicago`) and is required for `end_of_day`: the end of the
 * day is the organization's own local midnight. The server validates and canonicalises it and
 * refuses anything else with `invalid_request`. Switching back to `immediate` keeps the stored
 * zone and captures every outstanding authorization on the next job run.
 */
export const cardCaptureRequestSchema = z
    .object({
    timing: z.enum(CARD_CAPTURE_TIMING_VALUES),
    timeZone: z.string().trim().min(1).max(64).nullable().optional(),
})
    .strict()
    .refine((value) => value.timing === "immediate" || Boolean(value.timeZone), {
    message: "End-of-day capture needs the organization's time zone.",
    path: ["timeZone"],
});
export const cardCaptureResponseSchema = z.object({
    timing: z.enum(CARD_CAPTURE_TIMING_VALUES),
    timeZone: z.string().nullable(),
});
/**
 * `POST /v1/organizations/:organizationId/gifts/:giftId/cancel` — withdraw an end-of-day gift's
 * card authorization before it is captured. `donation.refund` plus step-up, like a refund; no
 * body. Nothing is charged, so nothing is refunded and no fee is kept or lost; no ledger entry is
 * written. Idempotent: cancelling a cancelled gift answers the same.
 *
 * Refusals: `gift_already_captured` (409) when the money was already taken — refund it instead;
 * `conflict` (409) when the gift has no authorization to withdraw (it was captured at once, is a
 * bank debit, failed, or is still waiting for the donor).
 */
export const giftCancelResponseSchema = z.discriminatedUnion("status", [
    z.object({
        giftId: z.string().uuid(),
        status: z.literal("cancelled"),
        cancelledAt: z.string(),
    }),
    /**
     * GH-948: 202, the processor holds the cancel for a person to approve in its dashboard. Nothing
     * has changed yet: the card is still authorized, and the gift updates from the processor's
     * webhook once the cancel is approved and runs.
     */
    processorApprovalSchema.extend({
        giftId: z.string().uuid(),
        status: z.literal("awaiting_processor_approval"),
        message: z.string(),
    }),
]);
/**
 * A gift's capture, on the gift detail (optional; absent for gifts with no card attempt).
 * `method`: how the intent was opened. `state`: `authorized` while the money is held,
 * `captured` once taken, `cancelled` when withdrawn or lapsed, null otherwise (still with the
 * donor, failed). `scheduledFor`: the earliest moment the capture job takes an `authorized` gift
 * (the organization's next local midnight, or sooner when the authorization would lapse); null
 * otherwise.
 */
export const giftCaptureSchema = z.object({
    method: z.enum(["automatic", "manual"]),
    state: z.enum(["authorized", "captured", "cancelled"]).nullable(),
    scheduledFor: z.string().nullable(),
});
/** `POST /v1/jobs/capture-authorized-gifts` — operator-guarded; the worker calls it every 15 minutes. */
export const captureJobResponseSchema = z.object({
    /** Authorizations looked at this run. */
    examined: z.number().int().nonnegative(),
    /** Asked the provider to capture; the webhook completes each gift. */
    captured: z.number().int().nonnegative(),
    /** Not due yet (the organization's day is not over). */
    waiting: z.number().int().nonnegative(),
    /** Already captured, cancelled or lapsed at the provider; left for its webhook. */
    settledElsewhere: z.number().int().nonnegative(),
    /** The provider could not be asked; retried on the next run. */
    failed: z.number().int().nonnegative(),
    /**
     * Manual-capture attempts found authorized by asking the provider, because no
     * `payment_intent.amount_capturable_updated` webhook reached us (GH-947 reconcile). Optional
     * so a response from before the reconcile still parses.
     */
    reconciled: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=card-capture.js.map